import * as React from 'react';
import { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import BasicDropdownOptionWrapper from '@tock/shared/components/inputs/basic-dropdown/BasicDropdownOptionWrapper';
import BasicDropdownTrigger from '@tock/shared/components/inputs/basic-dropdown/BasicDropdownTrigger';
import InputContainer from '@tock/shared/components/forms/InputContainer';
import Portal from '@tock/shared/components/Portal';
import clsx from 'clsx';

export interface PortalDropdownOptionProps<T> {
  option: T;
  isSelected?: boolean;
}

interface Props<T> {
  component: FunctionComponent<PortalDropdownOptionProps<T>>;
  contextClassName?: string;
  disabled?: boolean;
  fitContentWidth?: boolean;
  hideChevron?: boolean;
  isOptionSelected?(option: T, selectedOption: T): boolean;
  onOpen?(isOpen: boolean): void;
  onSelect(option: T): void;
  options: T[];
  optionsClassName?: string;
  position?: 'auto' | 'bottom' | 'top';
  selectedOption: T;
  testId?: string;
}

const PortalDropdown = <T,>({
  component,
  contextClassName,
  disabled = false,
  fitContentWidth = false,
  hideChevron = false,
  isOptionSelected = (option, selectedOption) => option === selectedOption,
  onOpen,
  onSelect,
  options,
  optionsClassName,
  position = 'auto',
  selectedOption,
  testId = 'portal-dropdown',
}: Props<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [resolvedPosition, setResolvedPosition] = useState<'bottom' | 'top'>(
    position === 'top' ? 'top' : 'bottom'
  );
  const [menuPosition, setMenuPosition] = useState<React.CSSProperties>();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const setOpen = (nextIsOpen: boolean) => {
    if (disabled) {
      return;
    }

    setIsOpen(nextIsOpen);
    onOpen?.(nextIsOpen);
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const reposition = () => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) {
        return;
      }

      const triggerRect = trigger.getBoundingClientRect();
      const menuHeight = menu.offsetHeight;
      const bottomSpace = window.innerHeight - triggerRect.bottom - 6;
      const topSpace = triggerRect.top - 6;
      const nextPosition: 'bottom' | 'top' =
        position === 'top' ||
        (position === 'auto' && menuHeight > bottomSpace && topSpace > bottomSpace)
          ? 'top'
          : 'bottom';

      setResolvedPosition(nextPosition);
      setMenuPosition({
        left: triggerRect.left - 6,
        top: nextPosition === 'bottom' ? triggerRect.bottom + 6 : triggerRect.top - menuHeight - 6,
        ...(fitContentWidth ? {} : { width: triggerRect.width + 12 }),
      });
    };

    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);

    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [fitContentWidth, isOpen, position]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        if (!disabled) {
          setIsOpen(false);
          onOpen?.(false);
        }
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [disabled, isOpen, onOpen]);

  const className = clsx(
    'Select',
    'Select--strip',
    'Select--minimal',
    'BasicDropdown',
    { 'is-open': isOpen },
    contextClassName
  );

  return (
    <div
      ref={rootRef}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setOpen(false);
        }
      }}
    >
      <InputContainer className={className} disabled={disabled}>
        <div ref={triggerRef}>
          <BasicDropdownTrigger
            isOpen={isOpen}
            disabled={disabled}
            onToggle={() => setOpen(!isOpen)}
            hideChevron={hideChevron}
            data-testid={`${testId}-trigger`}
          >
            {React.createElement(component, { option: selectedOption })}
          </BasicDropdownTrigger>
        </div>
      </InputContainer>

      <Portal isOpen={isOpen}>
        <div
          ref={menuRef}
          className={clsx(
            'BasicDropdown-options',
            'PortalDropdown-options',
            optionsClassName,
            `is-${resolvedPosition}`,
            contextClassName
          )}
          role="listbox"
          style={menuPosition}
          data-testid={testId}
        >
          <div className="BasicDropdown-scrollContext">
            {options.map((option, index) => (
              <BasicDropdownOptionWrapper
                component={component}
                isSelected={isOptionSelected(option, selectedOption)}
                key={index}
                onClick={(_event: React.MouseEvent<HTMLElement>, selected: T) => {
                  onSelect(selected);
                  setOpen(false);
                }}
                option={option}
                data-testid={`${testId}-option`}
              />
            ))}
          </div>
        </div>
      </Portal>
    </div>
  );
};

export default PortalDropdown;
